<template>
  <div class="card" data-panel-group="">
    <div
      class="card-header"
      :id="headingId"
    >
      <button
        class="btn-accordion collapsed"
        :class="headerStatusClass"
        type="button"
        v-b-toggle="collapseId"
        :aria-controls="collapseId"
      >
        <div class="market-project__content">
          <h3 class="market-project__title">
            <a
              href="#"
              @click.stop.prevent="onSeeDetailsClick"
            >
              {{ productTitle }}
            </a>
            <MarketplaceUnreadBadge :count="productBulbCount" />
          </h3>
          <div>
            Marketplace:
            <strong>{{ marketplaceName }}</strong>
          </div>

          <ul class="specs-grid">
            <li
              v-if="companyName"
            >
              Company Name:
              <strong>{{ companyName }}</strong>
            </li>

            <li
              v-for="(item, idx) in specItems"
              :key="`spec-${productId}-${idx}`"
            >
              {{ item.label }}:
              <strong>{{ item.value }}</strong>
            </li>
          </ul>
        </div>

        <div class="market-project__actions">
          <div class="market-project__info offer">
            <span class="deadline-info">
              <i class="icon-calendar"></i>
              Delivery date:
              <strong>{{ deliveryDateText }}</strong>
            </span>

            <span class="price-info">
              Price: <strong>{{ productPriceText }}</strong>
            </span>

            <p
              v-if="counterOffersCount > 0"
              class="mb-0"
            >
              {{ counterOffersCount }} counter offer{{ counterOffersCount === 1 ? '' : 's' }}
            </p>
          </div>

          <div class="status-multi">
            <h6>Status:</h6>
            <span
              v-if="isSaved"
              class="status saved"
            >
              Saved
            </span>
            <span v-if="showPublishedBadge"
              class="status published"
            >
              Published
            </span>

            <span
              v-if="showCounterOfferedBadge"
              class="status counteroffered"
            >
              Counter offered
            </span>

            <span
              v-if="hasApprovedOffer"
              class="status approved"
            >
              Approved
            </span>

            <span
              v-if="showThreadsBadge"
              class="status threads"
            >
              {{ threadsCount }} message thread{{ threadsCount === 1 ? '' : 's' }}
            </span>
          </div>
        </div>

        <ul class="offers-action">
          <li>
            <a
              class="btn-action"
              :class="{ 'btn-reject': isSaved }"
              href="#"
              @click.stop.prevent="onSaveClick"
            >
              <i :class="isSaved ? 'icon-unsave' : 'icon-save'"></i>
              {{ isSaved ? 'Unsave' : 'Save' }}
            </a>
          </li>

          <li>
            <a
              class="btn btn-action"
              href="#"
              @click.stop.prevent="onSeeDetailsClick"
            >
              <i class="icon-info"></i>
              See Details
              <MarketplaceUnreadBadge :count="productMessagesCount" />
            </a>
          </li>

          <li>
            <a
              class="btn-action"
              href="#"
              role="button"
              tabindex="0"
              @click.stop.prevent="openCounterOffer"
              @keydown.enter.stop.prevent="openCounterOffer"
              @keydown.space.stop.prevent="openCounterOffer"
            >
              <i class="icon-coffer"></i>
              Counter Offer
              <MarketplaceUnreadBadge :count="counterOfferBulbCount" />
            </a>
          </li>

          <li v-if="!hasApprovedOffer">
            <a
              class="btn-action btn-approve"
              href="#"
              :class="{ disabled: !canApproveProduct }"
              :aria-disabled="String(!canApproveProduct)"
              @click.stop.prevent="onApproveClick"
            >
              <i class="icon-approve"></i>
              {{ isApproving ? 'Approving…' : 'Approve' }}
            </a>
          </li>
        </ul>
      </button>
    </div>

    <b-collapse
      ref="offerCollapse"
      :id="collapseId"
      :accordion="accordionId"
      :visible="false"
      role="tabpanel"
      :aria-labelledby="headingId"
      @shown="onCollapseShown"
      @hidden="onCollapseHidden"
    >
      <div class="card-body">
        <h5>Product details:</h5>
        <p>{{ productDetail }}</p>

        <h5>Product advantages/features:</h5>
        <p>{{ productFeature }}</p>

        <div ref="counterOfferSection">
          <div class="coffers">
            <h3 class="mb-2">Your counter offer(s):</h3>

                <ul
                  v-if="counterOffers.length"
                  class="coffers__list"
                >
                  <li
                    v-for="(offer, idx) in counterOffers"
                    :key="offer.id || idx"
                    class="coffers__item"
                  >
                    <div class="form-block__body">
                      <div class="form-row-grid">
                        <label>Price:</label>
                        <span>
                          <strong>{{ formatOfferPrice(offer) }}</strong>
                          <MarketplaceUnreadBadge :count="offerBulb(offer)" />
                        </span>
                      </div>

                      <div class="form-row-grid">
                        <label>Terms:</label>
                        <div class="text">
                          <p
                            v-for="(p, pIdx) in offerTermsParagraphs(offer)"
                            :key="pIdx"
                          >
                            {{ p }}
                          </p>
                        </div>
                      </div>

                      <p
                        v-if="offer.__detailsLoading"
                        class="mb-2"
                        style="opacity: 0.7;"
                      >
                        Loading messages…
                      </p>

                      <p
                        v-if="offer.__detailsError"
                        class="mb-2"
                        style="color: #960c15;"
                      >
                        {{ offer.__detailsError }}
                      </p>

                      <MessageThread
                        :ref="`offerThread-${offer.id}`"
                        :messages="offer.messages"
                        :isSending="sendingOfferId === offer.id"
                        :meAvatar="meAvatar"
                        :submitOnEnter="true"
                        placeholder="Comment..."
                        @send="sendOfferMessage(offer, $event)"
                        @like="(msg) => handleLikeOfferMessage(offer, msg)"
                        @unlike="(msg) => handleUnlikeOfferMessage(offer, msg)"
                      />
                    </div>
                  </li>
                </ul>

                <div
                  v-else
                  class="form-block__body"
                >
                  <p class="mb-0">No counter offers yet.</p>
                </div>
              </div>

          <div class="submit-counter-offer">
            <h3
              v-if="!isCounterOfferCreationLocked"
              class="mb-2 mt-4"
            >
              Submit counter offer
            </h3>

            <div
              v-if="!isCounterOfferCreationLocked"
              class="form-block__body"
            >
              <div class="form-row-grid">
                <label>Your price:</label>
                <input
                  v-model="counterOfferPrice"
                  class="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder=""
                >
              </div>

              <div class="form-row-grid">
                <label>Your terms:</label>
                <textarea
                  v-model.trim="counterOfferTerms"
                  cols="30"
                  rows="4"
                  placeholder=""
                  required="required"
                  class="form-control"
                ></textarea>
              </div>

              <div class="btns-group--right">
                <button
                  class="btn btn-primary"
                  type="button"
                  :disabled="isSendingCounterOffer || !canSubmitCounterOffer"
                  @click.prevent="submitCounterOffer"
                >
                  {{ isSendingCounterOffer ? 'Submitting…' : 'Submit' }}
                </button>
              </div>

              <p
                v-if="counterOfferSuccessMessage"
                class="mt-2"
                style="color: #0BB86F;"
              >
                {{ counterOfferSuccessMessage }}
              </p>

              <p
                v-if="counterOfferErrorMessage"
                class="mt-2"
                style="color: #960c15;"
              >
                {{ counterOfferErrorMessage }}
              </p>
            </div>

            <p
              v-else
              class="mt-2"
              style="opacity: 0.7;"
            >
              New counter offers are unavailable after approval.
            </p>
          </div>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { BCollapse } from 'bootstrap-vue';
import moment from 'moment';

import MatchmakingService from '@/services/MatchmakingService';
import exception from '@/mixins/exception';
import MessageThread from '@/components/matchmaking/MessageThread.vue';
import MarketplaceUnreadBadge from '@/components/matchmaking/MarketplaceUnreadBadge.vue';
import marketplaceOfferDetails from '@/mixins/marketplaceOfferDetails';
import marketplaceProductFromResponse from '@/helpers/marketplaceProductResponse';

export default {
  name: 'BuyerProductCard',

  components: {
    BCollapse,
    MessageThread,
    MarketplaceUnreadBadge,
  },

  mixins: [exception, marketplaceOfferDetails],

  props: {
    product: {
      type: Object,
      required: true,
    },
    accordionId: {
      type: String,
      default: 'accordionOffers',
    },
  },

  data() {
    return {
      pendingPanel: null,
      isCollapseOpen: false,
      isOfferSyncing: false,
      counterOfferRetryTimers: [],

      messages: Array.isArray(this.product?.messages) ? [...this.product.messages] : [],
      isSendingProductMessage: false,

      counterOfferPrice: '',
      counterOfferTerms: '',
      isSendingCounterOffer: false,
      counterOfferSuccessMessage: '',
      counterOfferErrorMessage: '',
      counterOfferMessageTimerId: null,

      counterOffers: [],
      sendingOfferId: null,
      localOffersCount: Number(this.product?.offers_count
        || this.product?.offers?.length || 0) || 0,
      localThreadsCount: Number(this.product?.messages_count || 0) || 0,
      isApproving: false,
      localProductStatus: this.product?.status ?? null,
      localProductStatusName: this.product?.status_name ?? '',
    };
  },

  computed: {
    offerListKey() {
      return 'counterOffers';
    },

    productId() {
      return this.product?.id;
    },

    productBulbCount() {
      return this.$store.getters.productBulb('buyer', this.productId);
    },

    productMsgIds() {
      return (Array.isArray(this.messages) ? this.messages : [])
        .map((m) => m && m.id)
        .filter((id) => id !== undefined && id !== null);
    },

    counterOfferBulbCount() {
      return (Array.isArray(this.counterOffers) ? this.counterOffers : []).reduce(
        (total, offer) => total
          + this.$store.getters.productOfferBulb('buyer', offer && offer.id),
        0,
      );
    },

    productMessagesCount() {
      const total = Number(this.productBulbCount) || 0;
      const offers = Number(this.counterOfferBulbCount) || 0;
      const residual = Math.max(0, total - offers);
      const loaded = Number(
        this.$store.getters.productMessageBulb('buyer', this.productMsgIds),
      ) || 0;
      const live = Number(
        this.$store.getters.productLiveMessageBulb('buyer', this.productId),
      ) || 0;
      return Math.max(residual, loaded, live);
    },

    headingId() {
      return `headingOffers-${this.productId}`;
    },

    collapseId() {
      return `collapse-offer-${this.productId}`;
    },

    productTitle() {
      return this.product?.name || `Product #${this.productId}`;
    },

    marketplaceName() {
      return this.product?.marketplace?.name || 'Marketplace';
    },

    companyName() {
      return this.product?.company_name || '';
    },

    productDetail() {
      return this.product?.detail || '—';
    },

    productFeature() {
      return this.product?.feature || '—';
    },

    deliveryDateText() {
      const raw = this.product?.deadline_at;
      if (!raw) return '—';

      const m = moment(raw, [
        'DD/MM/YY',
        'DD/MM/YYYY',
        'YYYY-MM-DD',
        'YYYY-MM-DD HH:mm:ss',
        moment.ISO_8601,
      ], true);

      if (m.isValid()) return m.format('DD.MM.YYYY');
      return String(raw);
    },

    productPriceText() {
      const raw = this.product?.price;
      if (raw === null || raw === undefined) return '—';

      const str = String(raw).trim();
      if (str === '') return '—';

      const num = Number(str);
      if (!Number.isFinite(num)) return '—';

      return `${num} euro`;
    },

    specItems() {
      const data = Array.isArray(this.product?.data) ? this.product.data : [];

      return data
        .filter((f) => f && f.name)
        .slice(0, 10)
        .map((f) => ({
          label: f.name,
          value: this.formatFieldValue(f),
        }));
    },

    isSaved() {
      return Boolean(this.product?.saved);
    },

    counterOffersCount() {
      const byArray = Array.isArray(this.counterOffers) ? this.counterOffers.length : 0;
      return Math.max(Number(this.localOffersCount) || 0, byArray);
    },

    threadsCount() {
      if (this.messages.length) return 1;
      return this.localThreadsCount;
    },

    hasApprovedOffer() {
      return this.isProductApproved
        || this.counterOffers.some((offer) => this.isApprovedOffer(offer))
        || (Array.isArray(this.product?.offers)
          && this.product.offers.some((offer) => this.isApprovedOffer(offer)));
    },

    isProductApproved() {
      const statusName = String(this.localProductStatusName || this.product?.status_name || '')
        .toLowerCase();
      if (statusName.includes('approved')) return true;

      const statusId = Number(this.localProductStatus ?? this.product?.status);
      return statusId === 2;
    },

    canApproveProduct() {
      return Boolean(this.productId) && !this.isApproving && !this.isProductApproved;
    },

    isApprovedState() {
      return this.isProductApproved;
    },

    headerStatusClass() {
      if (this.isApprovedState) return 'approved';
      if (this.isSaved) return 'saved';
      return 'published';
    },

    showPublishedBadge() {
      return !this.isApprovedState && !this.isSaved;
    },

    showCounterOfferedBadge() {
      return !this.isApprovedState && this.counterOffersCount > 0;
    },

    showThreadsBadge() {
      return !this.isApprovedState && this.threadsCount > 0;
    },

    isCounterOfferCreationLocked() {
      return this.isApprovedState;
    },

    meAvatar() {
      const me = this.$store.getters.getMe;
      return me?.avatar || me?.photo || '';
    },

    canSubmitCounterOffer() {
      if (this.isCounterOfferCreationLocked) return false;
      const price = Number(this.counterOfferPrice);
      const hasPrice = Number.isFinite(price) && price > 0;
      const hasTerms = Boolean(String(this.counterOfferTerms || '').trim());
      return hasPrice && hasTerms;
    },
  },

  watch: {
    'product.status': {
      immediate: true,
      handler(val) {
        this.localProductStatus = val ?? null;
      },
    },

    'product.status_name': {
      immediate: true,
      handler(val) {
        this.localProductStatusName = val ?? '';
      },
    },

    'product.offers': {
      immediate: true,
      handler(val) {
        this.counterOffers = this.cloneOffers(val);
      },
    },
  },
  beforeDestroy() {
    this.clearCounterOfferRetryTimers();
    this.clearCounterOfferMessageTimer();
  },

  methods: {
    clearCounterOfferRetryTimers() {
      this.counterOfferRetryTimers.forEach((timerId) => clearTimeout(timerId));
      this.counterOfferRetryTimers = [];
    },

    showOfferCollapse() {
      const collapseRef = this.$refs.offerCollapse;
      if (collapseRef && typeof collapseRef.show === 'function') {
        collapseRef.show();
      } else {
        this.$root.$emit('bv::show::collapse', this.collapseId);
      }

      const collapseEl = document.getElementById(this.collapseId);
      if (collapseEl && !collapseEl.classList.contains('show')) {
        const trigger = this.$el && this.$el.querySelector(`[aria-controls="${this.collapseId}"]`);
        if (trigger && typeof trigger.click === 'function') {
          trigger.click();
        }
      }
    },

    hideOfferCollapse() {
      const collapseRef = this.$refs.offerCollapse;
      if (collapseRef && typeof collapseRef.hide === 'function') {
        collapseRef.hide();
      } else {
        this.$root.$emit('bv::hide::collapse', this.collapseId);
      }

      const collapseEl = document.getElementById(this.collapseId);
      if (collapseEl && collapseEl.classList.contains('show')) {
        const trigger = this.$el && this.$el.querySelector(`[aria-controls="${this.collapseId}"]`);
        if (trigger && typeof trigger.click === 'function') {
          trigger.click();
        }
      }
    },

    cloneOffers(val) {
      const src = Array.isArray(val) ? val : [];
      const cloned = JSON.parse(JSON.stringify(src));

      return cloned.map((o) => ({
        ...o,
        messages: Array.isArray(o?.messages) ? o.messages : [],
        __detailsLoaded: Boolean(Array.isArray(o?.messages) && o.messages.length),
        __detailsLoading: false,
        __detailsError: '',
      }));
    },

    offerBulb(offer) {
      return this.$store.getters.productOfferBulb('buyer', offer && offer.id);
    },

    offerUnreadIds() {
      return (Array.isArray(this.counterOffers) ? this.counterOffers : [])
        .filter((offer) => offer && offer.id
          && this.$store.getters.productOfferBulb('buyer', offer.id) > 0)
        .map((offer) => String(offer.id));
    },

    isOfferCollapseVisible() {
      if (!this.$el || !this.$el.isConnected) return false;
      const collapseEl = document.getElementById(this.collapseId);
      return Boolean(collapseEl
        && collapseEl.classList.contains('show')
        && !collapseEl.classList.contains('collapsing'));
    },

    async syncCounterOffers() {
      if (this.isOfferSyncing) return;
      this.isOfferSyncing = true;
      try {
        const unreadIds = this.offerUnreadIds();
        const offers = Array.isArray(this.counterOffers) ? this.counterOffers.slice() : [];
        const results = await Promise.all(offers.map(async (offer) => {
          const ok = await this.loadOfferDetails(offer, true);
          return { id: String(offer && offer.id), ok };
        }));

        await this.$nextTick();
        if (!this.isOfferCollapseVisible()) return;

        await Promise.all(results.map(async ({ id, ok }) => {
          if (!ok) return;
          const fresh = this.counterOffers.find((o) => String(o && o.id) === id);
          if (!fresh) return;
          if (unreadIds.includes(id)) await this.scrollToOfferThread(fresh.id);
          await this.markOfferMessagesRead(fresh);
        }));
      } finally {
        this.isOfferSyncing = false;
      }
    },

    formatOfferPrice(offer) {
      const value = offer?.price ?? offer?.amount ?? offer?.value;
      const currency = offer?.currency || 'euro';
      if (value === null || value === undefined || value === '') return '—';
      return `${value} ${currency}`;
    },

    offerTermsParagraphs(offer) {
      const raw = offer?.terms
        ?? offer?.term
        ?? offer?.detail
        ?? offer?.description
        ?? offer?.text
        ?? '';

      const str = String(raw || '').trim();
      if (!str) return ['—'];

      return str
        .split(/\n\s*\n|\r\n\s*\r\n|\r\s*\r|\n|\r/g)
        .map((p) => p.trim())
        .filter(Boolean);
    },

    isApprovedOffer(offer) {
      const statusName = String(offer?.status_name || offer?.status?.name || '').toLowerCase();
      if (statusName.includes('approved')) return true;

      const statusId = Number(offer?.status?.id ?? offer?.status);
      return statusId === 2 || statusId === 3;
    },

    async sendOfferMessage(offer, text) {
      if (!offer || !offer.id || !text) return;

      this.sendingOfferId = offer.id;

      try {
        const payload = {
          offer_id: offer.id,
          text,
        };

        const { data } = await MatchmakingService.marketplaceProductOfferMessage(payload);

        if (!Array.isArray(offer.messages)) this.$set(offer, 'messages', []);
        offer.messages.push(data);
      } catch (e) {
        const optimistic = {
          id: `tmp-${Date.now()}`,
          text,
          created_at: new Date().toISOString(),
          user: this.$store.getters.getMe || {},
          __optimistic: true,
        };

        if (!Array.isArray(offer.messages)) this.$set(offer, 'messages', []);
        offer.messages.push(optimistic);
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        this.sendingOfferId = null;
      }
    },
    formatFieldValue(field) {
      if (!field) return '—';

      if (Array.isArray(field.value)) {
        return field.value
          .map((v) => (v && typeof v === 'object' ? (v.name || v.value || v.title || '') : v))
          .filter(Boolean)
          .join(', ');
      }
      if (field.value && typeof field.value === 'object') {
        return field.value.name || field.value.value || field.value.title || '—';
      }
      return field.value != null ? String(field.value) : '—';
    },

    openCounterOffer() {
      const collapseEl = document.getElementById(this.collapseId);
      const isOpen = this.isCollapseOpen
        || (collapseEl && collapseEl.classList.contains('show'));

      if (isOpen) {
        if (this.counterOfferBulbCount > 0) {
          this.syncCounterOffers();
          return;
        }
        this.pendingPanel = null;
        this.clearCounterOfferRetryTimers();
        this.hideOfferCollapse();
        return;
      }

      this.pendingPanel = 'counter';
      this.clearCounterOfferRetryTimers();
      this.showOfferCollapse();
      this.counterOfferRetryTimers = [140, 320, 620].map((delay) => setTimeout(() => {
        if (!this.pendingPanel) return;
        const panelEl = document.getElementById(this.collapseId);
        if (!panelEl || !panelEl.classList.contains('show')) {
          this.showOfferCollapse();
        }
      }, delay));
    },

    onCollapseShown() {
      this.isCollapseOpen = true;
      this.syncCounterOffers();
      if (this.pendingPanel === 'counter') {
        this.$nextTick(() => {
          const section = this.$refs.counterOfferSection;
          if (section && typeof section.scrollIntoView === 'function') {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
      this.pendingPanel = null;
    },

    onCollapseHidden() {
      this.isCollapseOpen = false;
      this.pendingPanel = null;
      this.clearCounterOfferRetryTimers();
      this.clearCounterOfferMessageTimer();
      this.counterOfferSuccessMessage = '';
      this.counterOfferErrorMessage = '';
    },

    clearCounterOfferMessageTimer() {
      if (this.counterOfferMessageTimerId != null) {
        clearTimeout(this.counterOfferMessageTimerId);
        this.counterOfferMessageTimerId = null;
      }
    },

    scheduleCounterOfferMessageDismiss() {
      this.clearCounterOfferMessageTimer();
      this.counterOfferMessageTimerId = setTimeout(() => {
        this.counterOfferSuccessMessage = '';
        this.counterOfferErrorMessage = '';
        this.counterOfferMessageTimerId = null;
      }, 8000);
    },

    async handleLikeProductMessage(msg) {
      if (!msg?.id) return;
      try {
        await MatchmakingService.addLikeToMessage(msg.id);
        // eslint-disable-next-line no-underscore-dangle
        const m = this.messages.find((x) => x.id === msg.id || x.id === msg._key);
        if (m) {
          this.$set(m, 'liked', true);
          this.$set(m, 'is_liked', true);
          const cnt = Number(m?.likes_count ?? m?.likes ?? 0) + 1;
          this.$set(m, 'likes_count', cnt);
          this.$set(m, 'likes', cnt);
        }
      } catch (e) {
        this.handleException(e);
      }
    },

    async handleUnlikeProductMessage(msg) {
      if (!msg?.id) return;
      try {
        await MatchmakingService.removeLikeFromMessage(msg.id);
        // eslint-disable-next-line no-underscore-dangle
        const m = this.messages.find((x) => x.id === msg.id || x.id === msg._key);
        if (m) {
          this.$set(m, 'liked', false);
          this.$set(m, 'is_liked', false);
          const cnt = Math.max(0, Number(m?.likes_count ?? m?.likes ?? 1) - 1);
          this.$set(m, 'likes_count', cnt);
          this.$set(m, 'likes', cnt);
        }
      } catch (e) {
        this.handleException(e);
      }
    },

    async handleLikeOfferMessage(offer, msg) {
      if (!msg?.id || !offer?.messages) return;
      try {
        await MatchmakingService.addLikeToMessage(msg.id);
        // eslint-disable-next-line no-underscore-dangle
        const m = offer.messages.find((x) => x.id === msg.id || x.id === msg._key);
        if (m) {
          this.$set(m, 'liked', true);
          this.$set(m, 'is_liked', true);
          const cnt = Number(m?.likes_count ?? m?.likes ?? 0) + 1;
          this.$set(m, 'likes_count', cnt);
          this.$set(m, 'likes', cnt);
        }
      } catch (e) {
        this.handleException(e);
      }
    },

    async handleUnlikeOfferMessage(offer, msg) {
      if (!msg?.id || !offer?.messages) return;
      try {
        await MatchmakingService.removeLikeFromMessage(msg.id);
        // eslint-disable-next-line no-underscore-dangle
        const m = offer.messages.find((x) => x.id === msg.id || x.id === msg._key);
        if (m) {
          this.$set(m, 'liked', false);
          this.$set(m, 'is_liked', false);
          const cnt = Math.max(0, Number(m?.likes_count ?? m?.likes ?? 1) - 1);
          this.$set(m, 'likes_count', cnt);
          this.$set(m, 'likes', cnt);
        }
      } catch (e) {
        this.handleException(e);
      }
    },

    async onSaveClick() {
      if (!this.productId) return;
      try {
        if (this.isSaved) {
          await MatchmakingService.unsaveProduct(this.productId);
          this.$emit('unsaved', this.product);
        } else {
          await MatchmakingService.saveProduct(this.productId);
          this.$emit('saved', this.product);
        }
      } catch (e) {
        this.handleException(e);
        this.$emit('toast', {
          variant: 'danger',
          message: this.isSaved ? 'Failed to unsave' : 'Failed to save',
        });
      }
    },

    onSeeDetailsClick() {
      if (!this.productId) return;
      const query = {
        view: String(this.$route.query.view || 'products'),
      };
      if (this.$route.query.marketplace_id) {
        query.marketplace_id = String(this.$route.query.marketplace_id);
      }
      this.$router.push({
        name: 'profileMatchmakingBuyerProductDetails',
        params: { id: String(this.productId) },
        query,
      }).catch(() => {});
    },

    async onApproveClick() {
      if (!this.canApproveProduct) return;

      this.isApproving = true;
      try {
        const { data } = await MatchmakingService.updateMarketplaceProduct(this.productId, {
          status: 2,
        });

        const updatedProduct = marketplaceProductFromResponse(data);
        const nextStatus = Number(updatedProduct?.status);
        const normalizedStatus = Number.isFinite(nextStatus) && nextStatus > 0 ? nextStatus : 2;
        const normalizedStatusName = String(
          updatedProduct?.status_name
          || updatedProduct?.status?.name
          || (normalizedStatus === 2 ? 'approved' : this.localProductStatusName)
          || '',
        );

        this.localProductStatus = normalizedStatus;
        this.localProductStatusName = normalizedStatusName;

        this.$emit('approved', {
          ...this.product,
          ...(updatedProduct && typeof updatedProduct === 'object' ? updatedProduct : {}),
          status: normalizedStatus,
          status_name: normalizedStatusName,
        });
      } catch (e) {
        this.handleException(e);
      } finally {
        this.isApproving = false;
      }
    },

    async sendProductMessage(text) {
      if (!this.productId) return;

      this.isSendingProductMessage = true;
      try {
        const payload = {
          product_id: this.productId,
          text,
        };

        const res = await MatchmakingService.marketplaceProductMessage(payload);
        if (res && res.data) {
          this.messages.push(res.data);
        } else {
          this.messages.push({
            id: `local-${Date.now()}`,
            text,
            created_at: new Date().toISOString(),
            user: this.$store.getters.getMe || {},
          });
        }

        if (this.localThreadsCount === 0) this.localThreadsCount = 1;
      } catch (error) {
        this.handleException(error);
      } finally {
        this.isSendingProductMessage = false;
      }
    },

    async submitCounterOffer() {
      if (!this.productId) return;
      if (this.isCounterOfferCreationLocked) {
        this.clearCounterOfferMessageTimer();
        this.counterOfferSuccessMessage = '';
        this.counterOfferErrorMessage = 'Counter offers are unavailable after approval.';
        return;
      }
      if (this.isSendingCounterOffer || !this.canSubmitCounterOffer) return;

      this.clearCounterOfferMessageTimer();
      this.counterOfferSuccessMessage = '';
      this.counterOfferErrorMessage = '';
      this.isSendingCounterOffer = true;

      try {
        const payload = {
          product_id: this.productId,
          price: Number(this.counterOfferPrice),
          terms: String(this.counterOfferTerms || '').trim(),
        };

        const res = await MatchmakingService.createMarketplaceProductOffer(payload);
        this.counterOfferSuccessMessage = 'Counter offer submitted.';
        this.scheduleCounterOfferMessageDismiss();

        this.localOffersCount = (Number(this.localOffersCount) || 0) + 1;

        const raw = res?.data;
        const createdOffer = (raw && typeof raw === 'object')
          ? (raw.offer || raw.data || raw.item || raw)
          : null;

        if (createdOffer && createdOffer.id) {
          const exists = this.counterOffers.some((o) => String(o?.id) === String(createdOffer.id));
          if (!exists) {
            this.counterOffers.push({
              ...createdOffer,
              messages: Array.isArray(createdOffer?.messages) ? createdOffer.messages : [],
              // eslint-disable-next-line no-underscore-dangle
              __detailsLoaded: Boolean(Array.isArray(createdOffer?.messages)
                && createdOffer.messages.length),
              __detailsLoading: false,
              __detailsError: '',
            });
          }

          this.$nextTick(() => {
            const justAdded = this.counterOffers.find((o) => String(o?.id)
              === String(createdOffer.id));
            if (justAdded) this.loadOfferDetails(justAdded, true);
          });
        }

        this.counterOfferPrice = '';
        this.counterOfferTerms = '';
      } catch (error) {
        this.counterOfferErrorMessage = 'Failed to submit counter offer.';
        this.handleException(error);
      } finally {
        this.isSendingCounterOffer = false;
      }
    },
  },
};
</script>

<style scoped>
.coffers__item {
  margin-bottom: 20px;
}

.coffers__item:last-child {
  margin-bottom: 0;
}

.text p {
  margin: 0 0 10px 0;
}

.text p:last-child {
  margin-bottom: 0;
}

.submit-counter-offer {
  margin-left: 5%;
}

@media (max-width: 576px) {
  .submit-counter-offer {
    margin-left: 0;
  }
}
</style>
